import type { z } from 'zod'
import { env } from '@/lib/env'
import { ApiError } from './errors'
import { isErrorEnvelope, isPaginatedEnvelope, unwrap, type Paginated } from './envelope'
import { tokenStore, type TokenPair } from './tokens'

/**
 * fetch-based client for uc-core.
 *  - adds `Authorization: Bearer <accessToken>`
 *  - on 401 refreshes once (single-flight, POST /auth/refresh) and replays the request
 *  - unwraps the { success, data } envelope and throws a typed ApiError otherwise
 *  - optional zod schema validates the unwrapped data at the boundary
 */

export type Query = Record<
  string,
  string | number | boolean | null | undefined | ReadonlyArray<string | number>
>

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  query?: Query
  /** JSON-serialisable body, or a Blob/FormData sent as-is. */
  body?: unknown
  headers?: Record<string, string>
  /** Validate the unwrapped `data` (and type the result from the schema). */
  schema?: z.ZodType
  /** Skip the Authorization header and 401-refresh (login, refresh, public config). */
  anonymous?: boolean
  signal?: AbortSignal
  /** Default 20s. Sync batches pass a longer timeout. */
  timeoutMs?: number
}

type HeaderProvider = () => Record<string, string>
const headerProviders = new Set<HeaderProvider>()

/** Register extra headers on every request (e.g. the offline module adds X-Device-Id). */
export function addHeaderProvider(p: HeaderProvider): () => void {
  headerProviders.add(p)
  return () => headerProviders.delete(p)
}

type SessionExpiredListener = () => void
const expiredListeners = new Set<SessionExpiredListener>()
/** Fires when a refresh fails and the tokens are cleared. */
export function onSessionExpired(l: SessionExpiredListener): () => void {
  expiredListeners.add(l)
  return () => expiredListeners.delete(l)
}

export function buildUrl(path: string, query?: Query): string {
  const base = /^https?:\/\//.test(path)
    ? path
    : `${env.apiBaseUrl}${path.startsWith('/') ? '' : '/'}${path}`
  if (!query) return base
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v)) v.forEach((item) => params.append(k, String(item)))
    else params.append(k, String(v))
  }
  const qs = params.toString()
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base
}

// ---------- refresh (single flight) ----------

interface AuthTokensResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export function toTokenPair(r: AuthTokensResponse, now = Date.now()): TokenPair {
  return {
    accessToken: r.accessToken,
    refreshToken: r.refreshToken,
    expiresAt: now + r.expiresIn * 1000,
  }
}

let refreshing: Promise<TokenPair | null> | null = null

/** POST /auth/refresh with the stored refresh token. Concurrent callers share one request. */
export function refreshTokens(): Promise<TokenPair | null> {
  refreshing ??= (async () => {
    const current = tokenStore.get()
    if (!current) return null
    try {
      const data = await request<AuthTokensResponse>('/auth/refresh', {
        method: 'POST',
        body: { refreshToken: current.refreshToken },
        anonymous: true,
      })
      const pair = toTokenPair(data)
      tokenStore.set(pair)
      return pair
    } catch (e) {
      // Only a definite rejection ends the session; offline keeps the tokens for later.
      if (e instanceof ApiError && (e.kind === 'unauthorized' || e.kind === 'validation')) {
        tokenStore.clear()
        expiredListeners.forEach((l) => l())
        return null
      }
      throw e
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

// ---------- core ----------

async function send(path: string, opts: RequestOptions, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  headerProviders.forEach((p) => Object.assign(headers, p()))
  Object.assign(headers, opts.headers)
  let body: BodyInit | undefined
  if (opts.body instanceof Blob || opts.body instanceof FormData) {
    body = opts.body
  } else if (opts.body !== undefined) {
    headers['Content-Type'] ??= 'application/json'
    body = JSON.stringify(opts.body)
  }
  if (token && !opts.anonymous) headers.Authorization = `Bearer ${token}`

  const timeout = AbortSignal.timeout(opts.timeoutMs ?? 20_000)
  const signal = opts.signal ? AbortSignal.any([opts.signal, timeout]) : timeout
  try {
    return await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? (opts.body === undefined ? 'GET' : 'POST'),
      headers,
      body,
      signal,
    })
  } catch (cause) {
    const timedOut = timeout.aborted && !opts.signal?.aborted
    throw new ApiError({
      kind: timedOut ? 'timeout' : 'network',
      message: timedOut ? 'The server took too long to answer' : 'Network unavailable',
      path,
      cause,
    })
  }
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null
  const text = await res.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

/** Raw request: returns the parsed (still enveloped) body. Used by request() and the sync engine. */
export async function requestRaw(
  path: string,
  opts: RequestOptions = {},
): Promise<{ status: number; body: unknown }> {
  let tokens = opts.anonymous ? null : tokenStore.get()
  let res = await send(path, opts, tokens?.accessToken ?? null)

  if (res.status === 401 && !opts.anonymous && tokens) {
    tokens = await refreshTokens()
    if (tokens) res = await send(path, opts, tokens.accessToken)
  }

  const body = await parseBody(res)
  if (!res.ok || isErrorEnvelope(body)) throw ApiError.fromResponse(res.status, body, path)
  return { status: res.status, body }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body } = await requestRaw(path, opts)
  const data = unwrap<unknown>(body)
  if (opts.schema) {
    const parsed = opts.schema.safeParse(data)
    if (!parsed.success) {
      throw new ApiError({
        kind: 'invalid_response',
        message: `Unexpected response from ${path}`,
        path,
        body: parsed.error.issues,
      })
    }
    return parsed.data as T
  }
  return data as T
}

export async function requestPaginated<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<Paginated<T>> {
  const { body } = await requestRaw(path, { ...opts, method: opts.method ?? 'GET' })
  if (!isPaginatedEnvelope(body)) {
    throw new ApiError({
      kind: 'invalid_response',
      message: `Expected a paginated response from ${path}`,
      path,
      body,
    })
  }
  const summary = (body as { summary?: unknown }).summary
  return {
    data: body.data as T[],
    meta: body.meta,
    ...(summary && typeof summary === 'object' ? { summary: summary as Record<string, unknown> } : {}),
  }
}

type VerbOptions = Omit<RequestOptions, 'method' | 'body'>

/** With a schema the result type comes from the schema; without one, from the type argument. */
interface Verb {
  <S extends z.ZodType>(path: string, opts: VerbOptions & { schema: S }): Promise<z.output<S>>
  <T = unknown>(path: string, opts?: VerbOptions): Promise<T>
}
interface BodyVerb {
  <S extends z.ZodType>(
    path: string,
    body: unknown,
    opts: VerbOptions & { schema: S },
  ): Promise<z.output<S>>
  <T = unknown>(path: string, body?: unknown, opts?: VerbOptions): Promise<T>
}

const verb =
  (method: 'GET' | 'DELETE'): Verb =>
  (path: string, opts: VerbOptions = {}) =>
    request(path, { ...opts, method })
const bodyVerb =
  (method: 'POST' | 'PUT' | 'PATCH'): BodyVerb =>
  (path: string, body?: unknown, opts: VerbOptions = {}) =>
    request(path, { ...opts, method, body })

export const api = {
  get: verb('GET'),
  delete: verb('DELETE'),
  post: bodyVerb('POST'),
  put: bodyVerb('PUT'),
  patch: bodyVerb('PATCH'),
  paginated: requestPaginated,
}
